import './customer-lost-modal.css';
import { Button, Modal, Select, Spin, Tooltip, Typography } from 'antd';
import { CUSTOMER_LOST_REASON_DATA_DELETION, strings } from '../../../lang';
import TextArea from 'antd/es/input/TextArea';
import { useState } from 'react';
import { CustomerDeleteData, SalesChannel } from '../../../dto/model';
import { useLoadingGuard } from '../../../hooks/use-loading-guard';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { DoNotCloseTag } from '../../common/donotclose-tag';

export interface CustomerLostInfo {
  reasons: string[];
  description: string;
  shareContactData: boolean | null;
  isDataDeletionRequested: boolean;
}

export interface LostReasonSelectOption {
  value: string;
  label: string;
  englishLabel?: string;
  outcomeKey?: string;
}

export interface CustomerLostBodyProps {
  open: boolean;
  onOk: (customerLostInfo: CustomerLostInfo) => Promise<void>;
  onCancel: () => void;
  inboundCenter?: boolean | undefined;
  isAutodialerFlow?: boolean;
  salesChannel: SalesChannel;
  customerDeleteData: CustomerDeleteData;
  useConfigurableLostReasons: boolean;
  lostReasonsReady: boolean;
  lostReasonOptions: LostReasonSelectOption[];
}

export const CustomerLostModal = ({
  open,
  onOk,
  onCancel,
  inboundCenter,
  isAutodialerFlow,
  salesChannel,
  customerDeleteData: _customerDeleteData,
  useConfigurableLostReasons,
  lostReasonsReady,
  lostReasonOptions,
}: CustomerLostBodyProps) => {
  const [customerLostOptions, setCustomerLostOptions] = useState<string | null>(null);
  const [shareContactData, setShareContactData] = useState<boolean | null>(null);
  const [description, setDescription] = useState<string>('');
  const [isLoading, guard] = useLoadingGuard();
  const defaultErrorState = {
    tooManySelected: false,
    noSelected: false,
    noInput: false,
    tooMuchInput: false,
    noShareOptionSelected: false,
  };
  const [error, setError] = useState(defaultErrorState);
  const [isDataDeletionRequested, setIsDataDeletionRequested] = useState<boolean>(false);

  const listOfOptionsToShareContactData = [
    'Owns PV system',
    'Roof over 3 months finished',
    'Other (please specify in comment)',
    'Technically not feasible',
    'No residence',
    'No wifi/laptop',
    'Apartment building (Mehrfamilienhaus)',
  ];

  const isShareContactDataEnabled = () => salesChannel === SalesChannel.CrmSales;
  const isRevision = salesChannel === SalesChannel.Revision;

  const selectedLostReasonOption = customerLostOptions
    ? lostReasonOptions.find((o) => o.value === customerLostOptions)
    : undefined;

  const shareByLegacyCrmReasonList =
    isShareContactDataEnabled() &&
    ((customerLostOptions != null && listOfOptionsToShareContactData.includes(customerLostOptions)) ||
      (useConfigurableLostReasons &&
        selectedLostReasonOption?.outcomeKey === 'DefaultSalesChannelBehavior' &&
        listOfOptionsToShareContactData.includes(selectedLostReasonOption?.englishLabel ?? '')));

  const showShareContactDataSection =
    !!customerLostOptions &&
    (shareByLegacyCrmReasonList ||
      (useConfigurableLostReasons && selectedLostReasonOption?.outcomeKey === 'CustomerLostWithSellerInfoOptin'));

  const modalTitle = useConfigurableLostReasons
    ? strings.completeCaseModalTitle
    : isShareContactDataEnabled() || isRevision
      ? strings.looseCustomerButton
      : strings.modalTitleCustomerLost;

  const modalIntro = useConfigurableLostReasons
    ? strings.completeCaseModalText
    : isShareContactDataEnabled() || isRevision
      ? strings.modalTextCustomerLostRevision
      : strings.modalTextCustomerLost;

  const showReasonsLoading = useConfigurableLostReasons && !lostReasonsReady;
  const confirmDisabled = isLoading || (useConfigurableLostReasons && !lostReasonsReady);
  const isOtherSpecifyCommentReason = (reasonLabel: string | null | undefined): boolean => {
    const normalized = reasonLabel?.trim().toLowerCase();
    return (
      normalized === 'other (please specify in comment)' ||
      normalized === 'Sonstiges (bitte im Kommentar angeben)'
    );
  };

  const requiresComment = (): boolean => {
    if (!customerLostOptions) {
      return false;
    }

    if (!useConfigurableLostReasons) {
      return isOtherSpecifyCommentReason(customerLostOptions);
    }

    const selected = lostReasonOptions.find((o) => o.value === customerLostOptions);
    return (
      isOtherSpecifyCommentReason(selected?.englishLabel) ||
      isOtherSpecifyCommentReason(selected?.label) ||
      isOtherSpecifyCommentReason(customerLostOptions)
    );
  };

  const onOkPressed = async () => {
    if (!customerLostOptions) {
      setError({ ...defaultErrorState, noSelected: true });
      return;
    }

    if (requiresComment() && description.trim().length === 0) {
      setError({ ...defaultErrorState, noInput: true });
      return;
    }

    if (description?.trim()?.length > 255) {
      setError({ ...defaultErrorState, tooMuchInput: true });
      return;
    }

    if (showShareContactDataSection && shareContactData === null) {
      setError({ ...defaultErrorState, noShareOptionSelected: true });
      return;
    }

    await guard(async () => {
      try {
        const selected = lostReasonOptions.find((o) => o.value === customerLostOptions);
        const apiReason = selected?.englishLabel?.trim() ?? '';
        const reasonsForApi = apiReason !== '' ? [apiReason] : [customerLostOptions!];
        await onOk({
          reasons: reasonsForApi,
          description,
          shareContactData: shareContactData,
          isDataDeletionRequested: customerLostOptions === CUSTOMER_LOST_REASON_DATA_DELETION || isDataDeletionRequested,
        });
        cleanState();
      } catch (e) {
        console.error('Failed to mark customer as lost:', e);
      }
    });
  };

  const onCancelPressed = () => {
    cleanState();
    onCancel();
  };

  const cleanState = () => {
    setCustomerLostOptions(null);
    setShareContactData(null);
    setDescription('');
    setError({
      tooManySelected: false,
      noSelected: false,
      noInput: false,
      tooMuchInput: false,
      noShareOptionSelected: false,
    });
    setIsDataDeletionRequested(false);
  };

  const onLostReasonChange = (value: string) => {
    setCustomerLostOptions(value);
    setError((prev) => ({ ...prev, noSelected: false, noInput: false, tooMuchInput: false }));
    const opt = lostReasonOptions.find((o) => o.value === value);
    const needsShare =
      (isShareContactDataEnabled() && listOfOptionsToShareContactData.includes(value)) ||
      (useConfigurableLostReasons &&
        (opt?.outcomeKey === 'CustomerLostWithSellerInfoOptin' ||
          (opt?.outcomeKey === 'DefaultSalesChannelBehavior' &&
            listOfOptionsToShareContactData.includes(opt?.label ?? ''))));
    if (!needsShare) {
      setShareContactData(null);
    }
  };

  const customerLostButtons = () => {
    return (
      <>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '5px' }}>
          <Button key="cancel" onClick={onCancelPressed} disabled={isLoading}>
            {strings.cancel}
          </Button>
          <Button
            key="ok"
            type="primary"
            onClick={() => void onOkPressed()}
            disabled={confirmDisabled}
            loading={isLoading}
          >
            {inboundCenter ? strings.backToInboundCenter : isAutodialerFlow ? strings.autodialerCaseComplete : strings.saveAndOpenNextCase}
          </Button>
        </div>
      </>
    );
  };

  return (
    <Modal
      title={modalTitle}
      centered
      closable={false}
      maskClosable={false}
      keyboard={false}
      open={open}
      width={600}
      cancelText={strings.cancel}
      onCancel={() => onCancelPressed()}
      cancelButtonProps={{ disabled: isLoading }}
      footer={customerLostButtons()}
    >
      <span>{modalIntro}</span>
      <br />
      {error.noSelected && <div style={{ color: 'red' }}>{strings.customerLostAtLeastOneOptionError}</div>}
      {error.tooManySelected && <div style={{ color: 'red' }}>{strings.customerLostUpTo3OptionsError}</div>}
      {showReasonsLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '32px 0' }}>
          <Spin />
        </div>
      ) : (
        <>
          {showShareContactDataSection && (
            <Typography.Text style={{ display: 'inline-block', marginTop: '5px', fontWeight: 'bold' }}>
              {strings.customerLostShareDataLabel}
              <Tooltip title={strings.customerLostShareDataTooltip}>
                <QuestionCircleOutlined style={{ marginLeft: '5px' }} />
              </Tooltip>
            </Typography.Text>
          )}
          <Select
            className="cust-lost-select"
            placeholder={strings.selectAtLeastOneReason}
            options={lostReasonOptions}
            onChange={onLostReasonChange}
            value={customerLostOptions}
            status={error.noSelected === true || error.tooManySelected ? 'error' : ''}
          />

          {error.noInput && <div style={{ color: 'red' }}>{strings.customerLostNoCharactersError}</div>}
          {error.tooMuchInput && <div style={{ color: 'red' }}>{strings.customerLostTooManyCharactersError}</div>}
          <TextArea
            maxLength={255}
            onChange={(e: any) => {
              setDescription(e.target.value);
              setError((prev) => ({ ...prev, noInput: false, tooMuchInput: false }));
            }}
            rows={3}
            value={description}
            placeholder={strings.customerLostPlaceholder}
            status={error.noInput || error.tooMuchInput === true ? 'error' : ''}
          />

          {showShareContactDataSection && (
            <div style={{ marginTop: '20px' }}>
              {error.noShareOptionSelected && <div style={{ color: 'red' }}>{strings.customerLostShareDataNoOptionError}</div>}
              <Select
                style={{ width: '470px', marginBottom: '15px' }}
                placeholder={strings.customerLostShareDataPlaceholder}
                options={strings.customerLostShareDataEntries}
                onChange={setShareContactData}
                maxTagCount={1}
                value={shareContactData}
                status={error.noShareOptionSelected ? 'error' : ''}
                title={strings.customerLostShareDataPlaceholder}
              />
            </div>
          )}
        </>
      )}
      {isLoading && <DoNotCloseTag />}
    </Modal>
  );
};
